import React from 'react';

/**
 * Absolute-positioned watermark overlay for product imagery.
 *
 * Renders TWO layers on top of an image:
 *   1. A repeating diagonal SVG pattern with the shop name — subtle,
 *      readable, and hard to crop out.
 *   2. A tiny bottom-right pill reading "Reference image only".
 *
 * Purely CSS/SVG — no server-side image processing, no impact on page speed.
 * Set `size="sm"` on thumbnails so the pattern text remains legible.
 */
const WATERMARK_LINE = 'Trendy Closet · Reference Image Only · Actual product may vary';

export const Watermark = ({ size = 'md' }) => {
  // Tile size / font scale — smaller tile on tiny thumbnails, larger on product hero
  const tile = size === 'sm' ? 220 : size === 'lg' ? 420 : 320;
  const fontSize = size === 'sm' ? 11 : size === 'lg' ? 15 : 13;
  const pillCls =
    size === 'sm'
      ? 'text-[9px] px-2 py-0.5'
      : 'text-[10px] px-2.5 py-1';

  return (
    <div
      className="absolute inset-0 pointer-events-none select-none z-10"
      aria-hidden="true"
      data-testid="image-watermark"
    >
      {/* Diagonal repeating text pattern */}
      <svg
        className="absolute inset-0 w-full h-full opacity-40"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id={`tc-wm-shadow-${size}`} x="-10%" y="-30%" width="120%" height="160%">
            <feDropShadow dx="0" dy="0.4" stdDeviation="0.6" floodColor="#000000" floodOpacity="0.55" />
          </filter>
          <pattern
            id={`tc-wm-${size}`}
            patternUnits="userSpaceOnUse"
            width={tile}
            height={tile}
            patternTransform="rotate(-28)"
          >
            <text
              x="0"
              y={tile / 2}
              fontFamily="'Cormorant Garamond', 'Playfair Display', serif"
              fontSize={fontSize}
              fontStyle="italic"
              fill="#ffffff"
              letterSpacing="2.4"
              filter={`url(#tc-wm-shadow-${size})`}
            >
              {WATERMARK_LINE}
            </text>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#tc-wm-${size})`} />
      </svg>

      {/* Corner pill — always readable */}
      <div
        className={`absolute bottom-2.5 right-2.5 uppercase tracking-[0.16em] bg-charcoal/70 backdrop-blur text-ivory/95 ${pillCls}`}
      >
        Reference only
      </div>
    </div>
  );
};

export default Watermark;
